import { areEqualShallow, selector, StateXGetter } from '@cloudio-saas/statex';
import {
  baseCommonProps,
  commonProps,
  ExpressionValue,
  getManager,
  IconValue,
  ItemIdProp,
  Platform,
  PropertyDefinition,
} from 'componentregistry';
import { controllerFunctionsAtom } from 'controller/ControllerRegistry';
import {
  activeItemIdsAtom,
  hideDefaultPropsAtom,
  hmrAtom,
  itemAtom,
  itemPropPath,
  itemTypeAtom,
} from 'designer/atoms';
import { itemWithDefaultValues } from 'registry/Components';
import { isNull } from 'utils/isEmpty';
import { sidebarPropsFilterAtom } from '../../atoms';

function getAllProps({
  appUid,
  pageId,
  itemId,
  get,
  filter,
  visibleProps,
  platform,
}: {
  appUid: string;
  pageId: string;
  itemId: string;
  get: StateXGetter;
  filter?: string;
  visibleProps?: string[];
  platform: Platform;
}): Record<string, PropertyDefinition> {
  const item = get(itemAtom, { params: { appUid, pageId, itemId } });
  if (!item?.props) {
    // itemId might have just got updated... or removed due to undo/redo
    return {};
  }
  const hideDefaultProps = get(hideDefaultPropsAtom);
  const mgr = getManager(item.type);

  let allProps: Record<string, PropertyDefinition> = {
    ...mgr.props,
    ...ItemIdProp,
    ...(mgr.managerType === 'Component' && mgr.type !== 'Page'
      ? commonProps
      : baseCommonProps),
  };
  const keys = Object.keys(allProps) as string[];

  const functions = get(controllerFunctionsAtom, {
    params: { appUid, pageId },
  });

  allProps = keys.reduce((map, key) => {
    const prop: PropertyDefinition = allProps[key];

    if (
      prop.type === 'controllerFunction' &&
      !functions.length &&
      !prop.alwaysShow
    ) {
      return map;
    }
    const itemWithDefaults = itemWithDefaultValues(item);
    const visible = mgr.isPropertyVisible(key, itemWithDefaults.props, {
      appUid,
      itemId,
      pageId,
      mgr,
      platform,
    });

    if (!visible) {
      // if property is not visible then just skip it
      return map;
    }
    if (visibleProps) {
      if (visibleProps.includes(key)) {
        map[key] = prop;
      }
      return map;
    }
    // @ts-ignore TODO
    const val = item.props[key];
    if (
      hideDefaultProps &&
      (val === undefined || areEqualShallow(mgr.defaultValue[key], val))
    ) {
      return map;
    }

    if (filter) {
      const value = itemWithDefaults.props[key] as
        | string
        | ExpressionValue
        | IconValue
        | boolean
        | number;
      let matched = prop.label.toLowerCase().includes(filter);
      if (!(matched || isNull(value))) {
        switch (prop.type) {
          case 'ItemID':
            matched = item.itemId.toLowerCase().includes(filter);
            break;
          case 'expression':
          case 'booleanExpression':
            {
              const _val = value as ExpressionValue;
              matched =
                typeof _val === 'object' && typeof _val.value === 'string'
                  ? _val.value.toLowerCase().includes(filter)
                  : false;
            }
            break;
          case 'iconselect':
            {
              const _val = value as IconValue;
              matched =
                typeof _val === 'object' && typeof _val.iconName === 'string'
                  ? _val.iconName.toLowerCase().includes(filter)
                  : false;
            }
            break;
          default:
            matched = String(value).includes(filter);
            break;
        }
      }
      if (!matched && typeof prop.helpText === 'string') {
        matched = prop.helpText.toLowerCase().includes(filter);
      }
      if (!matched) {
        return map;
      }
    }
    map[key] = prop;
    return map;
  }, {} as Record<string, PropertyDefinition>);
  return allProps;
}

function groupByCategory(
  allProps: Record<string, PropertyDefinition>,
): Record<string, Record<string, PropertyDefinition>> {
  const keys = Object.keys(allProps) as string[];
  const propsByCategory = keys.reduce((map, key) => {
    const prop: PropertyDefinition = allProps[key];
    const category = prop.category ?? 'General';
    let props = map[category];
    if (!props) {
      props = {};
      map[category] = props;
    }
    props[key] = prop;
    return map;
  }, {} as Record<string, Record<string, PropertyDefinition>>);
  return propsByCategory;
}

function processForMultiEdit(
  platform: Platform,
  allProps: Record<string, PropertyDefinition>,
  appUid: string,
  pageId: string,
  filter: string,
  get: StateXGetter,
) {
  const ids = get(activeItemIdsAtom, {
    params: { appUid, pageId },
  });
  if (ids.length) {
    const allPropsList = ids.map((id) =>
      getAllProps({ platform, appUid, pageId, itemId: id, get, filter }),
    );
    const keys = Object.keys(allProps) as string[];
    keys.forEach((key) => {
      if (allProps[key].preventMultiEdit) {
        delete allProps[key];
        return;
      }
      for (let i = 0; i < ids.length; i++) {
        if (
          !allPropsList[i][key] ||
          allPropsList[i][key].preventMultiEdit ||
          allPropsList[i][key].type !== allProps[key].type
        ) {
          delete allProps[key];
          break;
        }
      }
    });
  }
}

export const visiblePropertiesSelector = selector<
  [Record<string, Record<string, PropertyDefinition>>, string],
  { visibleProps: string[] | undefined; platform: Platform }
>({
  path: [
    'transient',
    'designer',
    'visibleProps',
    ':appUid',
    ':pageId',
    ':itemId',
    ':type',
  ],
  get: ({ get, params }, { visibleProps, platform }) => {
    if (!params) return [{}, ''];
    const { appUid, pageId, itemId } = params as Record<string, string>;

    // HMR Support: to trigger re-run of this selector when any manager code changes
    get(hmrAtom);

    // property panel filter
    const origFilter = get(sidebarPropsFilterAtom) ?? '';

    const filter = origFilter.toLowerCase();

    const allProps = getAllProps({
      platform,
      appUid,
      pageId,
      itemId,
      get,
      filter,
      visibleProps,
    });

    processForMultiEdit(platform, allProps, appUid, pageId, filter, get);

    return [groupByCategory(allProps), origFilter];
  },
});

export const multiValueSelector = selector<{
  sameValue: boolean;
  multiEdit?: boolean;
}>({
  path: [
    'transient',
    'designer',
    'bulkValue',
    ':appUid',
    ':pageId',
    ':property',
  ],
  get: ({ get, params }) => {
    let sameValue = true;
    if (!params) return { sameValue, multiEdit: false };
    const { property, appUid, pageId } = params;
    const ids = get(activeItemIdsAtom, {
      params: { appUid, pageId },
    });
    if (ids.length > 1) {
      const values = ids.map((itemId) => {
        const value = get(itemPropPath, {
          params: { appUid, pageId, itemId, prop: property },
        });
        if (isNull(value)) {
          const type = get(itemTypeAtom, {
            params: { appUid, pageId, itemId },
          });
          const mgr = getManager(type);
          return mgr.defaultValue[property];
        }
        return value;
      });
      for (let i = 0; i < values.length; i++) {
        if (!areEqualShallow(values[i], values[0])) {
          sameValue = false;
          break;
        }
      }
      return { sameValue, multiEdit: true };
    }
    return { sameValue, multiEdit: false };
  },
});
